import React, { useRef, useEffect, useState } from "react";
import Select, { components } from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { getCFOSelectClassNames } from "./cfoSelectClassNames.js";

export const Dropdown = ({
	valuePrefix,
	options,
	dropdownWidth,
	selectedValue,
	setSelectedValue,
	status,
	alignRight,
	menuWidth,
	isCompressed,
	fontSize,
	menuPortalTarget,
	fullWidth,
	disabled: isDisabled,
	twoLine = false,
	inputId,
}) => {
	const [uniqueId] = React.useState(
		() => "select_" + Math.random().toFixed(5).slice(2),
	);
	const wrapperRef = useRef(null);
	const [controlWidth, setControlWidth] = useState(null);

	// Measure control width so menu can be at least as wide
	useEffect(() => {
		if (!wrapperRef.current) return;
		const el = wrapperRef.current;
		const ro = new ResizeObserver(([entry]) => {
			setControlWidth(entry.contentRect.width);
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	// Don't render if critical props are missing
	if (!options || !Array.isArray(options) || options.length === 0) {
		return null;
	}

	if (!setSelectedValue) {
		return null;
	}

	const classNames = getCFOSelectClassNames({ status });

	// Menu look: rounded container, soft shadow; selected option = light blue bg + left bar + circular check
	const layoutStyles = {
		control: (base) => ({
			...base,
			backgroundColor: "hsl(var(--surface-2))",
			minWidth: fullWidth ? "100%" : dropdownWidth ? `${dropdownWidth}px` : undefined,
			width: fullWidth ? "100%" : "fit-content",
			...(twoLine && { minHeight: "48px" }),
		}),
		menu: (base) => {
			let w;
			let mw;
			if (fullWidth && controlWidth) {
				w = `${controlWidth}px`;
				mw = w;
			} else {
				const explicit = menuWidth ?? dropdownWidth;
				w = explicit != null ? `${explicit}px` : undefined;
				mw = explicit == null && controlWidth != null ? `${controlWidth}px` : undefined;
			}
			return {
				...base,
				width: w,
				minWidth: mw,
				maxWidth: fullWidth && controlWidth ? `${controlWidth}px` : undefined,
				// Keep LTR so the menu list scrollbar stays on the right; alignRight is handled by left/right only.
				direction: "ltr",
				right: alignRight ? 0 : "auto",
				left: alignRight ? "auto" : 0,
				marginTop: 2,
				borderRadius: "6px",
				boxShadow: "0 2px 8px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)",
				overflow: "hidden",
				backgroundColor: "hsl(var(--surface))",
			};
		},
		menuList: (base) => ({
			...base,
			padding: "4px",
		}),
		option: (base, { isSelected, isFocused }) => ({
			...base,
			backgroundColor: isSelected ? "hsl(var(--primary-soft))" : isFocused ? "hsl(var(--surface-2))" : "hsl(var(--surface))",
			color: isSelected ? "hsl(var(--primary))" : "hsl(var(--text-2))",
			borderLeft: isSelected ? "3px solid hsl(var(--primary))" : "3px solid transparent",
			paddingLeft: isSelected ? "9px" : "12px",
			paddingTop: "10px",
			paddingBottom: "10px",
			paddingRight: "12px",
			cursor: "default",
		}),
		singleValue: (base) => ({
			...base,
			overflow: isCompressed ? "hidden" : "visible",
			textOverflow: isCompressed ? "ellipsis" : "clip",
			whiteSpace: isCompressed ? "nowrap" : "normal",
			maxWidth: isCompressed ? "80px" : "none",
		}),
		...(menuPortalTarget && {
			menuPortal: (base) => ({ ...base, zIndex: 9999 }),
		}),
	};

	return (
		<div
			ref={wrapperRef}
			className={`relative z-[30] ${fullWidth ? "w-full" : ""}`}
		>
			<Select
				id={uniqueId}
				inputId={inputId}
				classNamePrefix="cfo-select"
				unstyled
				classNames={classNames}
				styles={layoutStyles}
				value={selectedValue || null}
				isSearchable={false}
				isDisabled={isDisabled}
				name="color"
				options={options || []}
				onChange={(e) => {
					if (e && setSelectedValue) {
						setSelectedValue(e);
					}
				}}
				closeMenuOnSelect
				blurInputOnSelect
				menuPortalTarget={menuPortalTarget}
				components={{
					IndicatorSeparator: () => null,
					DropdownIndicator: (props) => (
						<components.DropdownIndicator {...props}>
							<FontAwesomeIcon
								icon={icon({
									name: "chevron-down",
									family: "sharp",
									style: "light",
								})}
								className="text-text-3 hover:text-text-2 text-xs cfo-select__dropdown-indicator"
							/>
						</components.DropdownIndicator>
					),
				}}
			formatOptionLabel={(e, { context }) => {
				const title = e.label;
				const subtitle = e.subtitle;
				if (context === "value") {
					return (
						<div className="flex flex-row">
							{valuePrefix && (
								<span className="text-text">{valuePrefix}</span>
							)}
							<div className="flex flex-col min-w-0">
								<span className="text-text">{title}</span>
								{twoLine && subtitle && (
									<span className="text-xs text-text-3 leading-tight">
										{subtitle}
									</span>
								)}
							</div>
						</div>
					);
				}
				const isSelected =
					selectedValue &&
					(selectedValue.value === e.value || selectedValue.label === e.label) &&
					context === "menu";
				// Optional hierarchy hints (used by the allocation owner picker): `depth` indents
				// the row to convey nesting; `isParent` styles branch nodes a touch stronger than
				// leaves. Both default to no-ops, so every other Dropdown renders unchanged.
				const hasTree = e.depth != null || e.isParent != null;
				const titleClass = hasTree
					? (e.isParent
						? "text-sm font-semibold text-text"
						: "text-sm font-normal text-text")
					: "text-sm font-medium text-text";
				return (
					<div
						className="flex flex-row items-start justify-between gap-2 w-full"
						style={e.depth ? { paddingLeft: e.depth * 16 } : undefined}
					>
						<div className="flex flex-col min-w-0 flex-1">
							<span className={titleClass}>
								{title}
							</span>
							{twoLine && subtitle && (
								<span className="text-xs text-text-3 mt-0.5">
									{subtitle}
								</span>
							)}
						</div>
						{isSelected && (
							<span
								className={`${twoLine ? "mt-3" : "mt-0.5"} flex-shrink-0 rounded-full bg-primary flex items-center justify-center`}
								style={{ width: 16, height: 16 }}
								aria-hidden
							>
								<FontAwesomeIcon
									icon={icon({ name: "check", style: "solid" })}
									className="text-white text-xs"
								/>
							</span>
						)}
					</div>
				);
			}}
			/>
		</div>
	);
};
